import type { FC, ReactNode } from 'react'
import type { MotionValue } from 'motion/react'

import { motion, useTransform } from 'motion/react'
import { cn } from '@/lib/utils'
import { pad } from '@/lib/format'
import { conjugation } from '@/constants/content'

type ConjugationItem = (typeof conjugation)[number]

const SETTLED_LAST = "'wdth' 125, 'wght' 860"
const SETTLED_OTHER = "'wdth' 100, 'wght' 420"

interface ConjugationLineProps {
    item: ConjugationItem
    index: number
    progress: MotionValue<number>
    range: Array<number>
}

// One line of "Unë jap. Ti jep. Ne japim.": it widens and gains weight while it holds the stage.
// The last line keeps the stage. Colours assume the blue manifesto environment.
export const ConjugationLine: FC<ConjugationLineProps> = ({ item, index, progress, range }): ReactNode => {
    const isLast = index === conjugation.length - 1
    const focus = useTransform(progress, range, isLast ? [0, 1] : [0, 1, 1, 0])
    const variation = useTransform(focus, (value) => `'wdth' ${(78 + 47 * value).toFixed(1)}, 'wght' ${Math.round(320 + 540 * value)}`)
    const opacity = useTransform(focus, [0, 1], [0.34, 1])
    const noteOpacity = useTransform(focus, [0, 1], [0.5, 1])

    return (
        <div className='grid grid-cols-12 items-baseline gap-x-5'>
            <motion.p
                style={{ opacity: noteOpacity }}
                className='col-span-12 font-serif text-lg italic text-jap-paper/85 md:col-span-2 md:text-xl'
            >
                <span className='mr-2 font-heading text-xs not-italic tabular-nums text-jap-gold'>
                    {pad(index + 1)}
                </span>

                {item.note}
            </motion.p>

            <motion.p
                style={{ fontVariationSettings: variation, opacity }}
                className={cn(
                    'col-span-12 whitespace-nowrap font-heading text-[14.5vw] leading-[0.92] tracking-[-0.045em] md:col-span-10 md:text-[10.5vw]',
                    isLast ? 'text-jap-gold' : 'text-jap-paper'
                )}
            >
                {item.line}
            </motion.p>
        </div>
    )
}

// The same three lines without motion, for visitors who prefer reduced motion.
export const ConjugationStatic: FC = (): ReactNode => {
    return (
        <div id='manifesti' className='flex flex-col gap-[2svh] bg-jap-cobalt px-5 py-28 text-jap-paper md:px-8 lg:px-12'>
            {conjugation.map((item, index) => {
                const isLast = index === conjugation.length - 1

                return (
                    <div key={item.id} className='grid grid-cols-12 items-baseline gap-x-5'>
                        <p className='col-span-12 font-serif text-lg italic text-jap-paper/85 md:col-span-2 md:text-xl'>
                            <span className='mr-2 font-heading text-xs not-italic tabular-nums text-jap-gold'>
                                {pad(index + 1)}
                            </span>

                            {item.note}
                        </p>

                        <p
                            style={{ fontVariationSettings: isLast ? SETTLED_LAST : SETTLED_OTHER }}
                            className={cn(
                                'col-span-12 whitespace-nowrap font-heading text-[14.5vw] leading-[0.92] tracking-[-0.045em] md:col-span-10 md:text-[10.5vw]',
                                isLast ? 'text-jap-gold' : 'text-jap-paper'
                            )}
                        >
                            {item.line}
                        </p>
                    </div>
                )
            })}
        </div>
    )
}
